import type { StepDirection } from './resultStep';

import type { NavigableResult } from '@/services/ask/selectors';

/** What the Research screen hands the pushed result screen. */
export type PendingResults = {
  results: NavigableResult[];
  /**
   * Selects and reports a result the reader stepped to, exactly as the sheet
   * and pane do, so the thread's highlight follows a step taken on the result screen.
   */
  onStep: (target: NavigableResult, direction: StepDirection) => void;
};

let pending: { resultId: string; value: PendingResults } | null = null;

/**
 * Hands the pushed result screen (`app/(secure)/(tabs)/(community)/community/ask-result.tsx`)
 * the result the reader tapped plus the rest of its turn's results, so the
 * screen can step between them, without putting them in the URL. See
 * `ROUTES.LIBRARY_CHAT_RESULT`'s comment for why: a route param reaches
 * Segment as a screen property, and nothing there knows a result carries
 * inquiry titles rather than something safe to report. The screen also sits
 * outside the Research screen's store provider, so the step callback is how a
 * step reaches that store.
 *
 * Single most-recent value only: `LibraryChatScreen` sets it immediately
 * before `router.push`. Keyed by the tapped `resultId` rather than consumed on
 * read, so a benign remount of the destination screen (a re-render that
 * changes its native header, an interrupted transition) reads the same value
 * again instead of finding it already cleared — that exact gap is what made a
 * result opened from Research come up "no longer available" before the
 * destination screen's header-visibility toggle, which was causing that
 * remount, was fixed to be static instead.
 */
export function setPendingResults(resultId: string, value: PendingResults): void {
  pending = { resultId, value };
}

/** `null` if no result was handed off for this `resultId` (e.g. a direct visit to the route, or a stale one from a previous navigation). */
export function takePendingResults(resultId: string): PendingResults | null {
  if (pending?.resultId !== resultId) {
    return null;
  }

  return pending.value;
}
